import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useStoreItem } from "../itemStore.store";
import {
  defaultImpactMatrix,
  useImpactMatrixStore
} from "./impactMatrices.store";
import { map, mapValues, orderBy, pickBy, reduce } from "lodash-es";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { useRelationStore } from "../relation/relations.store";
import { useInspectorView } from "@editor/views/inspector/inspectorView.store";
import { useElements } from "../element/elements";
import { getImpactMatrixTitle } from "./impactMatrixFunctions";
import { useFlowBranchStore } from "../flow/flowBranches.store";
import { createSharedComposable } from "@vueuse/core";
import { toUid, type Uid } from "@/helpers/utility/Id";
import type { IndexItem } from "../index/IndexItem";
import type { ResourceItem } from "../resource/ResourceItem";
import type { TargetItem } from "../target/TargetItem";

export const useImpactMatrix = useStoreItem(
  defaultImpactMatrix,
  useImpactMatrixStore,
  (_matrix, store) => {
    const { t } = useI18n();
    const inspectorView = useInspectorView();

    const elements = useElements();

    const elementUids = computed(() => {
      const matrix = _matrix.value;
      const uids = new Set(matrix?.elementUids || []);
      if (matrix?.allTargets || matrix?.allResources || matrix?.allIndices) {
        for (const element of elements.ordered) {
          if (matrix?.allTargets && element.type === "target") uids.add(element.uid);
          if (matrix?.allResources && element.type === "resource") uids.add(element.uid);
          if (matrix?.allIndices && element.type === "index") uids.add(element.uid);
        }
      }
      return uids;
    });

    const includedElements = computed(() =>
      pickBy(elements.byUid, (element): element is TargetItem | ResourceItem | IndexItem => {
        return elementUids.value?.has(element.uid as Uid<"target" | "resource" | "index">)
      })
    );

    const elementValues = computed(() => {
      // get curve slopes for last completed round if possible
      // (the results of the previous round are the base of the current value)
      const { elementValues, selectedRound } = useSimulationViewStore();

      return selectedRound
        ? elementValues
        : inspectorView.elementValues;
    });

    /**
     * Get relation influences based on target values
     */
    const relationSlopes = computed(() => {
      if (elementValues.value == null) return {};

      const relationSlopes =
        useRelationStore().getCurveSlopesForStartItemValues(elementValues.value);

      // remove undefined values
      return pickBy(relationSlopes, (influence) => influence != null);
    });

    const incomingFlowStrengths = computed((): Record<Uid<"target" | "resource" | "index">, number> => {
      const flowBranchStore = useFlowBranchStore();
      const incomingFlowBranches = flowBranchStore.incomingByElementUid;
      const elements = includedElements.value;

      return mapValues(elements, (element) => {
        const elementValue = elementValues.value[element.uid];
        if (!elementValue) return 0;
        if (elementValue.minValue == null || elementValue.maxValue == null) return 0;
        const elementValueSpan = elementValue.maxValue - elementValue.minValue;
        if (elementValueSpan == 0) return 0;

        return reduce(incomingFlowBranches[element.uid] || [], (sum, flowBranch) => {
          const branchValue = elementValues.value[flowBranch.uid];
          if (branchValue == null) return sum;
          return sum + Number(branchValue.value);
        }, 0);
      });
    });

    const outgoingFlowStrengths = computed((): Record<Uid<"target" | "resource" | "index">, number> => {
      const flowBranchStore = useFlowBranchStore();
      const outgoingFlowBranches = flowBranchStore.outgoingByElementUid;
      const elements = includedElements.value;

      return mapValues(elements, (element) => {
        const elementValue = elementValues.value[element.uid];
        if (!elementValue) return 0;
        if (elementValue.minValue == null || elementValue.maxValue == null) return 0;
        const elementValueSpan = elementValue.maxValue - elementValue.minValue;
        if (elementValueSpan == 0) return 0;
        
        return reduce(outgoingFlowBranches[element.uid] || [], (sum, flowBranch) => {
          const branchValue = elementValues.value[flowBranch.uid];
          if (branchValue == null) return sum;
          if (element.uid == "resource_0") console.log(flowBranch.uid, branchValue.value);
          return sum + Number(branchValue.value);
        }, 0);
      });
    });

    /**
     * Get the target influences, depending on the mode
     * @returns influences are >= 0
     */
    const relationInfluences = computed(() => {
      if (inspectorView.priorityMode === "slope") {
        return mapValues(relationSlopes.value, (value) =>
          Math.abs(value * 100)
        );
      } else {
        const relations = useRelationStore().items;
        return mapValues(relations, (relation) => relation.maxInfluence);
      }
    });

    /**
     * get the sum of all influences affecting the targets.
     * uses the influence set by the curve.
     * @returns influence by target (>= 0)
     */
    const passiveInfluences = computed(() => {
      const elements = includedElements.value;
      const matrix = _matrix.value;

      if (!matrix) return {};
      
      const incomingRelations = useRelationStore().activeByEndUid;

      return mapValues(elements, (element) => {
        return reduce(
          incomingRelations[element.uid] || [],
          (sum, relation) => {
            const endUid = toUid(relation.endItemType, relation.endItemId);
            if (!elementUids.value.has(endUid)) return sum;
            if (relationInfluences.value[relation.id] == null) return sum;
            return sum + relationInfluences.value[relation.id];
          },
          0
        );
      });
    });

    /**
     * Returns the amount of active influence by element
     * @returns influence by element
     */
    const activeInfluences = computed(() => {
      const matrix = _matrix.value;
      const elements = includedElements.value;

      if (!matrix) return {};

      const outgoingRelations = useRelationStore().activeByStartUid;

      return mapValues(elements, (element) => {
        return reduce(
          outgoingRelations[element.uid],
          (sum, relation) => {
            const endUid = toUid(relation.endItemType, relation.endItemId);
            if (!elementUids.value.has(endUid)) return sum;
            if (relationInfluences.value[relation.id] == null) return sum;
            return sum + relationInfluences.value[relation.id];
          },
          0
        );
      });
    });

    /**
     * Returns the passive influence of each target divided by the maxInfluence
     */
    const normalizedPassivePriorities = computed(() => {
      const matrix = _matrix.value;
      if (!matrix) return {};

      // only normalize impact relations
      if (matrix.relationType == "flow") {
        const outgoing = outgoingFlowStrengths.value;
        const incoming = incomingFlowStrengths.value;

        return mapValues(incoming, (incomingValue, _uid) => {
          const elementUid = _uid as Uid<"target" | "resource" | "index">;
          const outgoingValue = outgoing[elementUid];
          const balance = incomingValue + outgoingValue;

          const element = elementValues.value[elementUid];

          if (!element) return 0;

          // elements without limits can't be normalized
          const elementMinValue = element.minValue;
          const elementMaxValue = element.maxValue;
          if (elementMaxValue == null || elementMinValue == null) {
            return 0;
          }

          const elementMaxSpan = elementMaxValue - elementMinValue;

          const base = Math.abs(incomingValue) / elementMaxSpan;
          if (balance <= 0) return base;

          const remainingValue = (elementMaxSpan - (Number(element.value) - elementMinValue));
          if (remainingValue <= 0) return 1;
          
          const rest = 1 - base;
          return Math.min(balance / remainingValue, 1) * rest + base;
        });
      }

      const influences = orderBy(
        passiveInfluences.value,
        (value) => value,
        "desc"
      );
      if (influences.length === 0) return {};
      const maxInfluence = influences[0];

      return mapValues(passiveInfluences.value, (influenceValue) => {
        if (maxInfluence === 0) return 0;
        return influenceValue / maxInfluence;
      });
    });

    /**
     * Returns the active influence of each target divided by the maxInfluence
     */
    const normalizedActiveInfluences = computed(() => {
      const matrix = _matrix.value;
      if (!matrix) return {};

      // only normalize impact relations
      if (matrix.relationType == "flow") {
        const outgoing = outgoingFlowStrengths.value;
        const incoming = incomingFlowStrengths.value;

        return mapValues(outgoing, (outgoingValue, _uid) => {
          const elementUid = _uid as Uid<"target" | "resource" | "index">;
          const incomingValue = incoming[elementUid];
          const balance = outgoingValue + incomingValue;

          const element = elementValues.value[elementUid];

          if (!element) return 0;

          // elements without limits can't be normalized
          const elementMinValue = element.minValue;
          const elementMaxValue = element.maxValue;
          if (elementMaxValue == null || elementMinValue == null) {
            return 0;
          }

          const elementMaxSpan = elementMaxValue - elementMinValue;

          const base = Math.abs(outgoingValue) / elementMaxSpan;
          if (balance >= 0) return base;

          const absBalance = Math.abs(balance);
          const remainingValue = (Number(element.value) - elementMinValue);
          if (remainingValue <= 0) return 1;
          
          const rest = 1 - base;
          return Math.min(absBalance / remainingValue, 1) * rest + base;
        });
      }

      const orderedInfluences = orderBy(
        activeInfluences.value,
        (value) => value,
        "desc"
      );
      if (orderedInfluences.length === 0) return {};
      const maxInfluence = orderedInfluences[0];

      return mapValues(activeInfluences.value, (influenceValue) => {
        if (maxInfluence === 0) return 0;
        return influenceValue / maxInfluence;
      });
    });

    const sortedActivePriorities = computed(() => {
      const prioritiesWithElementUids = map(
        normalizedActiveInfluences.value,
        (value, elementUid) => {
          return {
            elementUid,
            value
          };
        }
      );
      return orderBy(prioritiesWithElementUids, "value", "desc");
    });

    const title = computed(() => {
      const matrix = _matrix.value;
      if (!matrix) return "";
      return getImpactMatrixTitle(matrix);
    });
    
    const matrixTypeTitle = computed(() => {
      return _matrix.value?.relationType == 'impact' ? t('labels.crossImpactMatrix') : t('labels.flowMatrix');
    });

    return {
      elementUids,
      includedElements,
      passivePriorities: normalizedPassivePriorities,
      activePriorities: normalizedActiveInfluences,
      sortedActivePriorities,
      matrixTypeTitle,
      title
    };
  }
);

function _useSelectedImpactMatrix() {
  const store = useImpactMatrixStore();
  return useImpactMatrix(() => store.selectedMatrixId);
}
export const useSelectedImpactMatrix = createSharedComposable(_useSelectedImpactMatrix);